import { SEASON } from './balance.ts';

/**
 * Days, weeks and seasons as pure arithmetic on milliseconds since the epoch, all in UTC. There is no
 * `Date` here: the same input gives the same key on the server, in a test and in a job.
 */

export const DAY_MS = 86_400_000;
export const WEEK_MS = 7 * DAY_MS;

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

/** Whole days since 1970-01-01 (UTC); negative before it. */
export function dayIndex(ms: number): number {
  return Math.floor(ms / DAY_MS);
}

/** The civil date of a day number (Howard Hinnant's algorithm). */
function civilFromDays(z0: number): { y: number; m: number; d: number } {
  const z = z0 + 719_468;
  const era = Math.floor(z / 146_097);
  const doe = z - era * 146_097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36_524) - Math.floor(doe / 146_096)) / 365,
  );
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return { y: m <= 2 ? y + 1 : y, m, d };
}

/** The day number of a civil date. */
function daysFromCivil(y0: number, m: number, d: number): number {
  const y = m <= 2 ? y0 - 1 : y0;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m > 2 ? m - 3 : m + 9) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}

/** `2026-10-21`: the UTC day a moment falls in. Daily challenges and the daily heist are keyed by it. */
export function dayKey(ms: number): string {
  const { y, m, d } = civilFromDays(dayIndex(ms));
  return `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;
}

/** The first moment of a `dayKey`, or `null` for a text that is not one. */
export function dayStartMs(key: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const ms = daysFromCivil(y, m, d) * DAY_MS;
  return dayKey(ms) === key ? ms : null;
}

/** Monday 00:00 UTC of the week a moment falls in. */
export function weekStartMs(ms: number): number {
  // 1970-01-01 was a Thursday: day 0 is 3 days after a Monday.
  const monday = Math.floor((dayIndex(ms) + 3) / 7) * 7 - 3;
  return monday * DAY_MS;
}

/** `2026-W43`: the ISO 8601 week (it starts on Monday and belongs to the year of its Thursday). */
export function weekKey(ms: number): string {
  const monday = weekStartMs(ms);
  const thursday = monday + 3 * DAY_MS;
  const { y } = civilFromDays(dayIndex(thursday));
  const firstThursday = weekStartMs(daysFromCivil(y, 1, 4) * DAY_MS) + 3 * DAY_MS;
  const week = Math.round((thursday - firstThursday) / WEEK_MS) + 1;
  return `${pad(y, 4)}-W${pad(week, 2)}`;
}

export interface SeasonSpan {
  /** 1-based. */
  index: number;
  /** `S1`, `S2`… */
  id: string;
  startMs: number;
  /** Exclusive. */
  endMs: number;
}

/** The season a moment falls in: eight weeks each, back to back from the anchor; earlier counts as season 1. */
export function seasonOf(ms: number): SeasonSpan {
  const length = SEASON.weeks * WEEK_MS;
  const offset = Math.max(0, Math.floor((ms - SEASON.anchorMs) / length));
  const startMs = SEASON.anchorMs + offset * length;
  return { index: offset + 1, id: `S${offset + 1}`, startMs, endMs: startMs + length };
}

export function seasonById(id: string): SeasonSpan | null {
  const match = /^S([1-9]\d{0,3})$/.exec(id);
  if (!match) return null;
  const index = Number(match[1]);
  const length = SEASON.weeks * WEEK_MS;
  const startMs = SEASON.anchorMs + (index - 1) * length;
  return { index, id, startMs, endMs: startMs + length };
}
